import request from './request'

/** POST `/api/admin/care-order/inspection-detail` 请求体 */
export interface CareOrderInspectionDetailRequest {
  /** 检查项主键（与 create-follow-up 的 order_item_id 一致） */
  order_item_id: number
  /** 订单主键；来自 care-order/detail 的 data.id */
  id?: number
  /** 切换历史日期时传 history[].result_id */
  result_id?: number
}

export type CareOrderInspectionRecordKind = 'initial' | 'followup'

/** 后台 history[].task_type；无则不填，Report ID 侧视为无匹配 */
export type CareOrderInspectionTaskType = 'order' | 'followup'

/** 单条历史检查记录（日期下拉项） */
export interface CareOrderInspectionHistoryEntry {
  key: string
  orderPrimaryId?: number
  inspectionId?: number
  serviceDate?: string
  serviceTimeText?: string
  assignmentText?: string
  recordKind: CareOrderInspectionRecordKind
  /** 后台原始 task_type；仅 order / followup，不猜测 */
  taskType?: CareOrderInspectionTaskType
  reportId?: string
  detail?: CareOrderInspectionDetailPayload
}

/** 检查详情（刷新 View Detail 下方内容） */
export interface CareOrderInspectionDetailPayload {
  report_id?: string
  follow_up_report_id?: string
  service_date?: string
  service_time_text?: string
  assignment_text?: string
  inspector_name?: string
  status_text?: string
  condition_rate?: string
  condition_summary?: string
  condition?: string
  condition_note?: string
  location?: string
  location_detail?: string
  location_note?: string
  task?: string
  task_label?: string
  task_note?: string
  time?: string
  time_label?: string
  time_note?: string
  general_description?: string | string[]
  follow_up_date?: string
  follow_up_time_text?: string
  follow_up_technician?: string
  follow_up_notes?: string | string[]
  /** 跟进报告专用照片（来自 follow_up_record.photos，勿与初检 photo_urls 混用） */
  follow_up_photo_urls?: string[]
  photo_urls?: string[]
  /** App 端录像 URL（与 photo_urls 合并展示，用于区分播放方式） */
  video_urls?: string[]
  record_kind?: CareOrderInspectionRecordKind
}

export interface CareOrderInspectionDetailResult {
  records: CareOrderInspectionHistoryEntry[]
  detail: CareOrderInspectionDetailPayload | null
}

function readStr(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = obj[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  }
  return undefined
}

function readNum(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = obj[key]
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.trunc(v)
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v)
      if (Number.isFinite(n) && n > 0) return Math.trunc(n)
    }
  }
  return undefined
}

function readStrArray(obj: Record<string, unknown>, ...keys: string[]): string[] | undefined {
  for (const key of keys) {
    const v = obj[key]
    if (Array.isArray(v)) {
      const list = v
        .map((x) => (typeof x === 'string' ? x.trim() : ''))
        .filter(Boolean)
      if (list.length) return list
    }
    if (typeof v === 'string' && v.trim()) return [v.trim()]
  }
  return undefined
}

/** 兼容 string / number / 单元素数组 / { value, label } 等 App 侧取值 */
function readScalarValue(raw: unknown): string | undefined {
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
  if (Array.isArray(raw) && raw.length) return readScalarValue(raw[0])
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    return readStr(
      o,
      'value',
      'label',
      'text',
      'name',
      'selected_value',
      'selected',
      'answer',
      'title',
      'display_text',
      'displayText',
    )
  }
  return undefined
}

/** admin_checklist set-value：routine 字段为 array<string>，多项用 · 连接展示 */
function readJoinedScalarValues(raw: unknown, separator = ' · '): string | undefined {
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (Array.isArray(raw) && raw.length) {
    const parts: string[] = []
    for (const item of raw) {
      const part = typeof item === 'string' ? item.trim() : readScalarValue(item)
      if (part) parts.push(part)
    }
    if (parts.length) return parts.join(separator)
  }
  return readScalarValue(raw)
}

function normalizeComponentTypeKey(raw: string | undefined): string {
  const key = (raw ?? '').trim().toLowerCase()
  if (key === 'single_choice') return 'single'
  if (key === 'multiple_choice') return 'multiple'
  return key
}

function appendGeneralDescriptionLine(
  payload: CareOrderInspectionDetailPayload,
  line: string,
): void {
  const trimmed = line.trim()
  if (!trimmed) return
  const existing = payload.general_description
  if (Array.isArray(existing)) {
    if (!existing.includes(trimmed)) existing.push(trimmed)
    return
  }
  if (typeof existing === 'string' && existing.trim()) {
    payload.general_description = [existing.trim(), trimmed]
    return
  }
  payload.general_description = [trimmed]
}

function readNoteValue(raw: unknown): string | undefined {
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return readStr(
      raw as Record<string, unknown>,
      'note',
      'notes',
      'description',
      'comment',
      'text',
    )
  }
  return undefined
}

function assignPayloadString(
  payload: CareOrderInspectionDetailPayload,
  key: keyof CareOrderInspectionDetailPayload,
  value: string | undefined,
): void {
  if (!value) return
  const existing = payload[key]
  if (typeof existing === 'string' && existing.trim()) return
  ;(payload as Record<string, unknown>)[key as string] = value
}

function applyRoutineValuesStructure(
  payload: CareOrderInspectionDetailPayload,
  bag: Record<string, unknown>,
): void {
  const fieldDefs: Array<{
    keys: string[]
    summary: keyof CareOrderInspectionDetailPayload
    plain: keyof CareOrderInspectionDetailPayload
    note: keyof CareOrderInspectionDetailPayload
  }> = [
    {
      keys: ['condition', 'conditions'],
      summary: 'condition_summary',
      plain: 'condition',
      note: 'condition_note',
    },
    {
      keys: ['location', 'locations'],
      summary: 'location_detail',
      plain: 'location',
      note: 'location_note',
    },
    { keys: ['task', 'tasks'], summary: 'task_label', plain: 'task', note: 'task_note' },
    { keys: ['time', 'times'], summary: 'time_label', plain: 'time', note: 'time_note' },
  ]

  for (const def of fieldDefs) {
    for (const key of def.keys) {
      const value = readJoinedScalarValues(bag[key])
      if (!value) continue
      assignPayloadString(payload, def.summary, value)
      assignPayloadString(payload, def.plain, value)
      for (const noteKey of [
        `${key}_note`,
        `${key}_notes`,
        `${key}_description`,
        `${key}_desc`,
        `${key}_remark`,
        `${key}_remarks`,
        `${key}_comment`,
        `${key}_text`,
      ]) {
        const note = readNoteValue(bag[noteKey]) ?? readJoinedScalarValues(bag[noteKey])
        assignPayloadString(payload, def.note, note)
      }
      break
    }
  }
}

function applyGroupedInspectionFieldNotes(
  payload: CareOrderInspectionDetailPayload,
  bag: Record<string, unknown>,
): void {
  for (const groupKey of [
    'notes',
    'note_map',
    'field_notes',
    'descriptions',
    'remarks',
    'comments',
  ] as const) {
    const group = bag[groupKey]
    if (!group || typeof group !== 'object' || Array.isArray(group)) continue
    const g = group as Record<string, unknown>
    assignPayloadString(
      payload,
      'condition_note',
      readJoinedScalarValues(g.condition ?? g.conditions),
    )
    assignPayloadString(
      payload,
      'location_note',
      readJoinedScalarValues(g.location ?? g.locations),
    )
    assignPayloadString(payload, 'task_note', readJoinedScalarValues(g.task ?? g.tasks))
    assignPayloadString(payload, 'time_note', readJoinedScalarValues(g.time ?? g.times))
    const general = readJoinedScalarValues(
      g.general ?? g.general_description ?? g.remark ?? g.remarks,
    )
    if (general) appendGeneralDescriptionLine(payload, general)
  }
}

/** routine 也可能以 components + field_type=condition/location/task/time 存储（与 set-value 回显一致） */
function applyRoutineComponentsByFieldType(
  payload: CareOrderInspectionDetailPayload,
  raw: unknown,
): void {
  if (!Array.isArray(raw)) return
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const typeKey = normalizeComponentTypeKey(
      readStr(row, 'field_type', 'field_type_text', 'type') ??
        readStr(row, 'component', 'component_name', 'name', 'key'),
    )
    if (!typeKey) continue

    const value = readJoinedScalarValues(
      row.selected_values ?? row.selected_value ?? row.selected ?? row.values ?? row.value,
    )
    const note = readStr(row, 'note', 'notes', 'description', 'comment')

    if (typeKey === 'condition' || typeKey === 'conditions') {
      assignPayloadString(payload, 'condition_summary', value)
      assignPayloadString(payload, 'condition', value)
      assignPayloadString(payload, 'condition_note', note)
    } else if (typeKey === 'location' || typeKey === 'locations') {
      assignPayloadString(payload, 'location_detail', value)
      assignPayloadString(payload, 'location', value)
      assignPayloadString(payload, 'location_note', note)
    } else if (typeKey === 'task' || typeKey === 'tasks') {
      assignPayloadString(payload, 'task_label', value)
      assignPayloadString(payload, 'task', value)
      assignPayloadString(payload, 'task_note', note)
    } else if (typeKey === 'time' || typeKey === 'times') {
      assignPayloadString(payload, 'time_label', value)
      assignPayloadString(payload, 'time', value)
      assignPayloadString(payload, 'time_note', note)
    }
  }
}

function applyComponentsInspectionValues(
  payload: CareOrderInspectionDetailPayload,
  raw: unknown,
): void {
  if (!Array.isArray(raw)) return
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const componentName = readStr(row, 'component', 'component_name', 'name', 'key') ?? ''
    const fieldType = normalizeComponentTypeKey(
      readStr(row, 'field_type', 'field_type_text', 'type'),
    )
    const blob = [componentName, fieldType].filter(Boolean).join(' ').toLowerCase()
    if (!blob) continue

    const value =
      readStr(row, 'selected_value', 'selected', 'value', 'answer', 'text', 'label') ??
      readJoinedScalarValues(
        row.selected_values ?? row.selected_value ?? row.values ?? row.value,
      )
    const note = readStr(row, 'note', 'notes', 'description', 'comment')

    if (
      fieldType === 'photo' ||
      fieldType === 'image' ||
      blob.includes('photo') ||
      blob.includes('image')
    ) {
      mergeComponentMediaUrls(payload, collectComponentMediaUrls(row, value), 'photo')
      continue
    }

    if (fieldType === 'video' || blob.includes('video') || blob.includes('录像')) {
      mergeComponentMediaUrls(payload, collectComponentMediaUrls(row, value, true), 'video')
      continue
    }

    if (blob.includes('condition') && blob.includes('rate')) {
      assignPayloadString(payload, 'condition_rate', value)
    } else if (blob.includes('condition')) {
      assignPayloadString(payload, 'condition_summary', value)
      assignPayloadString(payload, 'condition', value)
      assignPayloadString(payload, 'condition_note', note)
    } else if (blob.includes('location')) {
      assignPayloadString(payload, 'location_detail', value)
      assignPayloadString(payload, 'location', value)
      assignPayloadString(payload, 'location_note', note)
    } else if (blob.includes('task')) {
      assignPayloadString(payload, 'task_label', value)
      assignPayloadString(payload, 'task', value)
      assignPayloadString(payload, 'task_note', note)
    } else if (blob.includes('time')) {
      assignPayloadString(payload, 'time_label', value)
      assignPayloadString(payload, 'time', value)
      assignPayloadString(payload, 'time_note', note)
    } else if (
      fieldType === 'single' ||
      fieldType === 'multiple' ||
      (componentName && value)
    ) {
      appendGeneralDescriptionLine(payload, `${componentName}: ${value}`)
    } else if (
      blob.includes('general') ||
      blob.includes('remark') ||
      (blob.includes('description') && !componentName)
    ) {
      if (value) appendGeneralDescriptionLine(payload, value)
    }
  }
}

function readNestedBag(obj: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const v = obj[key]
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>
  return null
}

function applyStructuredInspectionValues(
  payload: CareOrderInspectionDetailPayload,
  raw: unknown,
): void {
  if (!raw || typeof raw !== 'object') return
  const bag = raw as Record<string, unknown>
  applyRoutineValuesStructure(payload, bag)
  applyGroupedInspectionFieldNotes(payload, bag)
  readNestedInspectionValueFields(payload, bag)
  applyRoutineComponentsByFieldType(payload, bag.components)
  applyComponentsInspectionValues(payload, bag.components)
  const nestedComponents = readNestedBag(bag, 'values') ?? readNestedBag(bag, 'checklist_values')
  if (nestedComponents) {
    applyRoutineComponentsByFieldType(payload, nestedComponents.components)
    applyComponentsInspectionValues(payload, nestedComponents.components)
    applyRoutineValuesStructure(payload, nestedComponents)
  }
}

function collectComponentMediaUrls(
  row: Record<string, unknown>,
  value: string | undefined,
  videoOnly = false,
): string[] {
  const list: string[] = []
  const fromUrls = readPhotoUrls(
    row,
    videoOnly ? 'videos' : 'photos',
    videoOnly ? 'video_urls' : 'images',
    'photo_urls',
    'attachments',
    'upload_images',
    'files',
    'media',
    ...(videoOnly ? (['video_list'] as const) : []),
  )
  if (fromUrls) list.push(...fromUrls)
  const scalar =
    readStr(
      row,
      videoOnly ? 'video_url' : 'url',
      'image_url',
      'photo_url',
      'video_url',
      'path',
      'src',
      'file_url',
    ) ?? value
  if (scalar) list.push(scalar)
  for (const entryKey of ['values', 'selected_values', 'value', 'selected_value']) {
    const entry = row[entryKey]
    if (Array.isArray(entry)) {
      for (const part of entry) {
        const u = typeof part === 'string' ? part.trim() : readScalarValue(part)
        if (u) list.push(u)
      }
    }
  }
  return list
}

function mergeComponentMediaUrls(
  payload: CareOrderInspectionDetailPayload,
  urls: string[],
  kind: 'photo' | 'video',
): void {
  if (!urls.length) return
  const seen = new Set<string>()
  const deduped = urls.filter((url) => {
    if (!url || seen.has(url)) return false
    seen.add(url)
    return true
  })
  if (!deduped.length) return
  payload.photo_urls = mergePhotoUrlLists(payload.photo_urls, deduped) ?? deduped
  if (kind === 'video') {
    payload.video_urls = mergePhotoUrlLists(payload.video_urls, deduped) ?? deduped
  }
}

/** App 端上传照片/录像：兼容字符串 URL 与 { url / image_url / file_url } 对象 */
function readPhotoUrls(obj: Record<string, unknown>, ...keys: string[]): string[] | undefined {
  for (const key of keys) {
    const v = obj[key]
    if (Array.isArray(v) && v.length) {
      const list: string[] = []
      for (const item of v) {
        if (typeof item === 'string' && item.trim()) {
          list.push(item.trim())
          continue
        }
        if (item && typeof item === 'object') {
          const row = item as Record<string, unknown>
          const url = readStr(
            row,
            'url',
            'image_url',
            'imageUrl',
            'photo_url',
            'photoUrl',
            'video_url',
            'videoUrl',
            'file_url',
            'fileUrl',
            'path',
            'src',
          )
          if (url) list.push(url)
        }
      }
      if (list.length) return list
    }
    if (typeof v === 'string' && v.trim()) {
      const parts = v
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
      if (parts.length) return parts
    }
  }
  return undefined
}

const PHOTO_FIELD_KEY_RE =
  /(?:^|_)(url|photo|image|file|path|src|attachment|media|video)(?:s|_list|_urls)?$/i

function collectPhotoUrlsDeep(raw: unknown, depth = 0, seen = new Set<unknown>()): string[] {
  if (depth > 7 || raw == null) return []
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return []
    if (
      /^(https?:)?\/\//i.test(s) ||
      s.startsWith('/') ||
      s.startsWith('data:') ||
      s.startsWith('blob:')
    ) {
      return [s]
    }
    if (/[,;\n]/.test(s)) {
      return s
        .split(/[,;\n]/)
        .map((part) => part.trim())
        .filter(Boolean)
    }
    return []
  }
  if (Array.isArray(raw)) {
    const out: string[] = []
    for (const item of raw) out.push(...collectPhotoUrlsDeep(item, depth + 1, seen))
    return out
  }
  if (typeof raw !== 'object') return []
  if (seen.has(raw)) return []
  seen.add(raw)
  const o = raw as Record<string, unknown>
  const out: string[] = []
  for (const [key, value] of Object.entries(o)) {
    if (
      PHOTO_FIELD_KEY_RE.test(key) ||
      key === 'photos' ||
      key === 'images' ||
      key === 'videos' ||
      key === 'attachments' ||
      key === 'files'
    ) {
      out.push(...collectPhotoUrlsDeep(value, depth + 1, seen))
    }
  }
  return out
}

function mergePhotoUrlLists(existing: string[] | undefined, extra: string[]): string[] | undefined {
  if (!extra.length) return existing
  const out = existing ? [...existing] : []
  const seen = new Set(out)
  for (const url of extra) {
    if (!url || seen.has(url)) continue
    seen.add(url)
    out.push(url)
  }
  return out.length ? out : undefined
}

const INSPECTION_PHOTO_BASE_URL =
  (import.meta.env.VITE_BASE_URL || 'https://hm.back.escroud.com').replace(/\/$/, '')

/** 将 inspection-detail 返回的相对路径转为可访问的完整图片 URL */
export function normalizeCareOrderInspectionPhotoUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
  }
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${INSPECTION_PHOTO_BASE_URL}${path}`
}

export function normalizeCareOrderInspectionPhotoUrls(urls: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of urls) {
    const normalized = normalizeCareOrderInspectionPhotoUrl(raw)
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    out.push(normalized)
  }
  return out
}

const INSPECTION_VIDEO_URL_RE = /\.(?:mp4|mov|webm|m4v|avi|mkv|3gp)(?:[?#]|$)/i

/** 根据 URL 路径判断是否为录像（App 端上传常见后缀） */
export function careOrderInspectionMediaUrlIsVideo(url: string): boolean {
  const trimmed = url.trim()
  if (!trimmed) return false
  try {
    const pathname = /^https?:\/\//i.test(trimmed)
      ? new URL(trimmed).pathname
      : (trimmed.split(/[?#]/)[0] ?? trimmed)
    return INSPECTION_VIDEO_URL_RE.test(pathname)
  } catch {
    return INSPECTION_VIDEO_URL_RE.test(trimmed)
  }
}

export interface CareOrderInspectionDisplayMediaItem {
  url: string
  isVideo: boolean
}

function displayMediaFromUrlLists(
  photoUrls: string[] | undefined,
  videoUrls?: string[] | undefined,
): CareOrderInspectionDisplayMediaItem[] {
  const photos = normalizeCareOrderInspectionPhotoUrls(photoUrls ?? [])
  const videos = normalizeCareOrderInspectionPhotoUrls(videoUrls ?? [])
  const videoSet = new Set(videos)
  const seen = new Set<string>()
  const out: CareOrderInspectionDisplayMediaItem[] = []
  for (const url of [...photos, ...videos]) {
    if (!url || seen.has(url)) continue
    seen.add(url)
    out.push({
      url,
      isVideo: videoSet.has(url) || careOrderInspectionMediaUrlIsVideo(url),
    })
  }
  return out
}

/** 检验详情 Photos 区展示项：图片与 App 端录像混排 */
export function careOrderInspectionDisplayMediaFromDetail(
  detail: CareOrderInspectionDetailPayload | null | undefined,
): CareOrderInspectionDisplayMediaItem[] {
  return displayMediaFromUrlLists(detail?.photo_urls, detail?.video_urls)
}

/** 跟进区块 Photos：仅 follow_up_record 照片 */
export function careOrderInspectionFollowUpDisplayMediaFromDetail(
  detail: CareOrderInspectionDetailPayload | null | undefined,
): CareOrderInspectionDisplayMediaItem[] {
  return displayMediaFromUrlLists(detail?.follow_up_photo_urls)
}

/** 只认后台 task_type；其它值视为无匹配（不猜测） */
export function readCareOrderInspectionTaskType(
  raw: Record<string, unknown>,
): CareOrderInspectionTaskType | undefined {
  const v = (readStr(raw, 'task_type', 'taskType') ?? '').trim().toLowerCase()
  if (!v) return undefined
  if (v === 'followup' || v === 'follow_up' || v === 'follow-up') return 'followup'
  if (v === 'order') return 'order'
  return undefined
}

function inferRecordKind(raw: Record<string, unknown>): CareOrderInspectionRecordKind {
  const taskType = readCareOrderInspectionTaskType(raw)
  if (taskType === 'followup') return 'followup'
  if (taskType === 'order') return 'initial'

  const blob = [
    readStr(raw, 'record_kind', 'record_type', 'kind', 'type'),
    readStr(raw, 'status', 'status_text'),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  if (
    blob.includes('follow_up') ||
    blob.includes('followup') ||
    blob.includes('follow-up') ||
    blob.includes('fu_') ||
    blob.includes('follow')
  ) {
    return 'followup'
  }
  return 'initial'
}

/**
 * 主单 / 跟进 Report ID：严格按 history[].task_type 取对应 report_id。
 * 无匹配 task_type 或无 report_id → 空字符串（展示层显示 —）。
 */
export function careOrderInspectionReportIdFromHistory(
  records: Array<Pick<CareOrderInspectionHistoryEntry, 'taskType' | 'reportId'>>,
  taskType: CareOrderInspectionTaskType,
): string {
  for (const rec of records) {
    if (rec.taskType !== taskType) continue
    const id = rec.reportId?.trim()
    if (id) return id
  }
  return ''
}

function readNestedInspectionValueFields(
  payload: CareOrderInspectionDetailPayload,
  raw: unknown,
): void {
  if (!raw || typeof raw !== 'object') return
  const bag = raw as Record<string, unknown>
  applyRoutineValuesStructure(payload, bag)
  if (!payload.condition_summary) {
    payload.condition_summary = readJoinedScalarValues(
      bag.condition_summary ??
        bag.conditionSummary ??
        bag.condition_title ??
        bag.condition_name ??
        bag.selected_condition ??
        bag.condition_value ??
        bag.condition,
    )
  }
  if (!payload.condition) {
    payload.condition = readJoinedScalarValues(
      bag.condition ?? bag.condition_text ?? bag.condition_value ?? bag.selected_condition,
    )
  }
  if (!payload.condition_note) {
    payload.condition_note =
      readNoteValue(bag.condition_note ?? bag.conditionNote) ??
      readScalarValue(bag.condition_description ?? bag.condition_desc)
  }
  if (!payload.location_detail) {
    payload.location_detail = readJoinedScalarValues(
      bag.location_detail ?? bag.locationDetail ?? bag.location_value ?? bag.selected_location,
    )
  }
  if (!payload.location) {
    payload.location = readJoinedScalarValues(
      bag.location ?? bag.location_text ?? bag.location_value ?? bag.selected_location,
    )
  }
  if (!payload.location_note) {
    payload.location_note =
      readNoteValue(bag.location_note ?? bag.locationNote) ??
      readScalarValue(bag.location_description)
  }
  if (!payload.task_label) {
    payload.task_label = readJoinedScalarValues(
      bag.task_label ?? bag.taskLabel ?? bag.task_value ?? bag.selected_task,
    )
  }
  if (!payload.task) {
    payload.task = readJoinedScalarValues(
      bag.task ?? bag.task_text ?? bag.task_value ?? bag.selected_task,
    )
  }
  if (!payload.task_note) {
    payload.task_note =
      readNoteValue(bag.task_note ?? bag.taskNote) ?? readScalarValue(bag.task_description)
  }
  if (!payload.time_label) {
    payload.time_label = readJoinedScalarValues(
      bag.time_label ?? bag.timeLabel ?? bag.time_value ?? bag.selected_time,
    )
  }
  if (!payload.time) {
    payload.time = readJoinedScalarValues(
      bag.time ?? bag.time_text ?? bag.time_value ?? bag.selected_time,
    )
  }
  if (!payload.time_note) {
    payload.time_note =
      readNoteValue(bag.time_note ?? bag.timeNote) ?? readScalarValue(bag.time_description)
  }
  applyComponentsInspectionValues(payload, bag.components)
}

/** 合并多份 inspection 详情（后者覆盖前者） */
export function mergeCareOrderInspectionDetailPayloads(
  ...sources: Array<CareOrderInspectionDetailPayload | null | undefined>
): CareOrderInspectionDetailPayload | null {
  return mergeInspectionPayloads(...sources)
}

function mergeInspectionPayloads(
  ...sources: Array<CareOrderInspectionDetailPayload | null | undefined>
): CareOrderInspectionDetailPayload | null {
  const out: CareOrderInspectionDetailPayload = {}
  for (const src of sources) {
    if (!src) continue
    for (const [key, v] of Object.entries(src) as Array<
      [keyof CareOrderInspectionDetailPayload, CareOrderInspectionDetailPayload[keyof CareOrderInspectionDetailPayload]]
    >) {
      if (Array.isArray(v)) {
        if (!v.length) continue
        if (
          (key === 'photo_urls' || key === 'follow_up_photo_urls') &&
          Array.isArray(out[key]) &&
          (out[key] as string[]).length
        ) {
          const existing = out[key] as string[]
          const seen = new Set(existing)
          out[key] = [...existing, ...v.filter((url) => !seen.has(url))] as never
        } else {
          out[key] = [...v] as never
        }
      } else if (typeof v === 'string' && v.trim()) {
        out[key] = v as never
      } else if (v != null && v !== '') {
        out[key] = v as never
      }
    }
  }
  const hasData = Object.values(out).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? out : null
}

function readFormFieldValueNotes(raw: unknown): { value?: string; note?: string } {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>
    if ('value' in o || 'notes' in o || 'note' in o) {
      return {
        value: readScalarValue(o.value),
        note: readStr(o, 'notes', 'note', 'description'),
      }
    }
  }
  const value = readJoinedScalarValues(raw)
  return value ? { value } : {}
}

/** admin_care_order §7：report.form_values / result.metadata.form_values */
function parseFormValuesPayload(raw: unknown): CareOrderInspectionDetailPayload | null {
  if (!raw || typeof raw !== 'object') return null
  const bag = raw as Record<string, unknown>
  const payload: CareOrderInspectionDetailPayload = {}

  const rate = bag.rate_condition ?? bag.condition_rate ?? bag.rate
  if (rate != null && rate !== '') payload.condition_rate = String(rate)

  const condition = readFormFieldValueNotes(bag.condition)
  if (condition.value) {
    payload.condition_summary = condition.value
    payload.condition = condition.value
  }
  if (condition.note) payload.condition_note = condition.note

  const location = readFormFieldValueNotes(bag.location)
  if (location.value) {
    payload.location_detail = location.value
    payload.location = location.value
  }
  if (location.note) payload.location_note = location.note

  const task = readFormFieldValueNotes(bag.task)
  if (task.value) {
    payload.task_label = task.value
    payload.task = task.value
  }
  if (task.note) payload.task_note = task.note

  const time = readFormFieldValueNotes(bag.time)
  if (time.value) {
    payload.time_label = time.value
    payload.time = time.value
  }
  if (time.note) payload.time_note = time.note

  const general = bag.general_description
  if (typeof general === 'string' && general.trim()) {
    payload.general_description = [general.trim()]
  } else if (Array.isArray(general)) {
    const lines = general.map((x) => String(x).trim()).filter(Boolean)
    if (lines.length) payload.general_description = lines
  }

  const hasData = Object.values(payload).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? payload : null
}

/** admin_care_order §7：item.report_display */
function parseReportDisplayPayload(rd: Record<string, unknown>): CareOrderInspectionDetailPayload | null {
  const payload: CareOrderInspectionDetailPayload = {}

  assignPayloadString(payload, 'report_id', readStr(rd, 'report_id', 'reportId'))

  const dateTimeText = readStr(rd, 'date_time_text', 'dateTimeText')
  if (dateTimeText) {
    payload.service_time_text = dateTimeText
    assignPayloadString(payload, 'service_date', readStr(rd, 'date_time', 'dateTime') ?? dateTimeText)
  }

  assignPayloadString(payload, 'status_text', readStr(rd, 'status_text', 'statusText'))

  const inspector = rd.inspector
  if (inspector && typeof inspector === 'object') {
    const name = readStr(inspector as Record<string, unknown>, 'name', 'label', 'display_name')
    if (name) {
      payload.inspector_name = name
      payload.assignment_text = name
    }
  }

  const fields = rd.fields
  if (Array.isArray(fields)) {
    for (const field of fields) {
      if (!field || typeof field !== 'object') continue
      const f = field as Record<string, unknown>
      const key = (readStr(f, 'key') ?? '').toLowerCase()
      if (key === 'rate_condition' || key === 'condition_rate') {
        payload.condition_rate =
          readStr(f, 'value_text') ?? readScalarValue(f.value) ?? payload.condition_rate
      } else if (key === 'condition') {
        payload.condition_summary = readStr(f, 'summary_text') ?? readStr(f, 'value_text')
        payload.condition = payload.condition_summary
        payload.condition_note = readStr(f, 'notes_text')
      } else if (key === 'location') {
        payload.location_detail = readStr(f, 'summary_text')
        payload.location = payload.location_detail
        payload.location_note = readStr(f, 'notes_text')
      } else if (key === 'task') {
        payload.task_label = readStr(f, 'summary_text')
        payload.task = payload.task_label
        payload.task_note = readStr(f, 'notes_text')
      } else if (key === 'time') {
        payload.time_label = readStr(f, 'summary_text')
        payload.time = payload.time_label
        payload.time_note = readStr(f, 'notes_text')
      }
    }
  }

  const photos = readPhotoUrls(rd, 'photos')
  if (photos?.length) payload.photo_urls = photos

  const general = readStr(rd, 'general_description', 'generalDescription')
  if (general) payload.general_description = [general]

  const hasData = Object.values(payload).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? payload : null
}

/** care-order/detail items[].result */
function parseCareOrderDetailItemResult(
  result: Record<string, unknown>,
): CareOrderInspectionDetailPayload | null {
  let detail: CareOrderInspectionDetailPayload | null = null

  const photos = readPhotoUrls(result, 'photos')
  if (photos?.length) detail = { photo_urls: photos }

  const notes = readStr(result, 'notes')
  if (notes) detail = mergeInspectionPayloads(detail, { general_description: [notes] })

  const submitted = readStr(result, 'submitted_at')
  if (submitted) {
    detail = mergeInspectionPayloads(detail, {
      service_date: submitted,
      service_time_text: submitted,
    })
  }

  const statusText = readStr(result, 'status_text', 'statusText')
  if (statusText) {
    detail = mergeInspectionPayloads(detail, { status_text: statusText })
  }

  const metadata = result.metadata
  if (metadata && typeof metadata === 'object') {
    const m = metadata as Record<string, unknown>
    detail = mergeInspectionPayloads(detail, parseFormValuesPayload(m.form_values))
    detail = mergeInspectionPayloads(detail, parseDetailPayload(m))
  }

  detail = mergeInspectionPayloads(detail, parseFormValuesPayload(result.form_values))
  detail = mergeInspectionPayloads(detail, parseDetailPayload(result))

  return detail
}

/** inspection-detail data.item 或 care-order/detail 订单项上的 report/report_display/result */
function parseCareOrderInspectionItemPayload(
  item: Record<string, unknown>,
): CareOrderInspectionDetailPayload | null {
  let detail: CareOrderInspectionDetailPayload | null = null

  const reportDisplay = item.report_display
  if (reportDisplay && typeof reportDisplay === 'object') {
    detail = mergeInspectionPayloads(
      detail,
      parseReportDisplayPayload(reportDisplay as Record<string, unknown>),
    )
  }

  const report = item.report
  if (report && typeof report === 'object') {
    const r = report as Record<string, unknown>
    detail = mergeInspectionPayloads(detail, parseFormValuesPayload(r.form_values))
    detail = mergeInspectionPayloads(detail, parseDetailPayload(r))
    const photos = readPhotoUrls(r, 'photos')
    if (photos?.length) {
      detail = detail ?? {}
      detail.photo_urls = mergePhotoUrlLists(detail.photo_urls, photos)
    }
    const submitted = readStr(r, 'submitted_at')
    if (submitted) {
      detail = detail ?? {}
      if (!detail.service_date) detail.service_date = submitted
      if (!detail.service_time_text) detail.service_time_text = submitted
    }
  }

  const result = item.result
  if (result && typeof result === 'object') {
    detail = mergeInspectionPayloads(
      detail,
      parseCareOrderDetailItemResult(result as Record<string, unknown>),
    )
  }

  detail = mergeInspectionPayloads(
    detail,
    parseFollowUpRecordPayload(item.follow_up_record ?? item.followUpRecord),
  )

  return detail
}

/** care-order/detail 的 items[] 单条 → View Details 初始数据 */
export function parseCareOrderDetailItemInspection(
  item: unknown,
): CareOrderInspectionDetailPayload | null {
  if (!item || typeof item !== 'object') return null
  const raw = item as Record<string, unknown>

  let merged = parseCareOrderInspectionItemPayload(raw)

  for (const key of [
    'inspection_detail',
    'inspection',
    'detail',
    'inspection_result',
    'inspection_record',
    'current_inspection',
    'latest_inspection',
    'last_inspection',
    'routine_inspection',
    'inspection_data',
    'result_data',
    'item_values',
    'inspection_values',
    'selected_values',
    'submitted_values',
    'checklist_values',
    'values',
    'answer',
    'submission',
    'form_data',
    'report_content',
    'inspection_report',
    'inspection_content',
    'checklist_summary',
  ] as const) {
    merged = mergeInspectionPayloads(merged, parseDetailPayload(raw[key]))
  }

  for (const key of [
    'photos',
    'images',
    'inspection_photos',
    'photo_urls',
    'attachments',
    'upload_images',
  ] as const) {
    merged = mergeInspectionPayloads(merged, parseDetailPayload({ photo_urls: raw[key] }))
  }

  return merged
}

function parseAdminCareOrderHistoryEntry(
  raw: unknown,
  index: number,
): CareOrderInspectionHistoryEntry | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const resultId = readNum(o, 'result_id', 'resultId')
  const dateText = readStr(o, 'date_text', 'dateText')
  const dateRaw = readStr(o, 'date', 'date_time', 'service_date')
  const serviceDate = dateText ?? dateRaw ?? ''
  if (!serviceDate && resultId == null) return null

  const key = resultId != null ? `hist-r${resultId}` : `hist-${index}-${serviceDate}`
  const taskType = readCareOrderInspectionTaskType(o)

  return {
    key,
    inspectionId: resultId,
    serviceDate,
    serviceTimeText: readStr(o, 'date_time_text', 'dateTimeText', 'service_time_text'),
    assignmentText: readStr(o, 'assignment_text', 'inspector_name', 'inspector'),
    recordKind: inferRecordKind(o),
    taskType,
    reportId: readStr(o, 'report_id', 'reportId'),
  }
}

/** 解析单条 inspection 详情（订单项、历史记录或 detail 节点） */
export function parseCareOrderInspectionDetailPayload(
  raw: unknown,
): CareOrderInspectionDetailPayload | null {
  return parseDetailPayload(raw)
}

/**
 * 解析后端 follow_up_record（仅映射到 follow_up_*，不污染初检 photo_urls / inspector）。
 * 空字符串 / 空数组不写入，由展示层显示 — / 隐藏。
 */
function parseFollowUpRecordPayload(
  raw: unknown,
): CareOrderInspectionDetailPayload | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const out: CareOrderInspectionDetailPayload = {}

  const reportId = readStr(
    o,
    'follow_up_report_id',
    'followUpReportId',
    'fu_report_id',
  )
  if (reportId) out.follow_up_report_id = reportId

  const date = readStr(o, 'follow_up_date', 'followUpDate', 'date', 'service_date')
  if (date) out.follow_up_date = date

  const timeText = readStr(
    o,
    'follow_up_time_text',
    'followUpTimeText',
    'time_text',
    'service_time_text',
  )
  if (timeText) out.follow_up_time_text = timeText

  let techName = readStr(
    o,
    'follow_up_technician',
    'followUpTechnician',
    'technician_name',
    'assignment_text',
  )
  const technician = o.technician
  if (!techName && technician && typeof technician === 'object') {
    techName = readStr(
      technician as Record<string, unknown>,
      'name',
      'label',
      'display_name',
      'displayName',
      'username',
    )
  } else if (!techName && typeof technician === 'string' && technician.trim()) {
    techName = technician.trim()
  }
  if (techName) out.follow_up_technician = techName

  const notes = readStrArray(o, 'follow_up_notes', 'followUpNotes', 'follow_up_note', 'notes', 'note')
  if (notes?.length) out.follow_up_notes = notes

  const photos = readPhotoUrls(o, 'photos', 'photo_urls', 'photoUrls', 'images', 'follow_up_photos')
  if (photos?.length) out.follow_up_photo_urls = photos

  const hasData = Object.values(out).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? out : null
}

function applyFollowUpRecordFromObject(
  payload: CareOrderInspectionDetailPayload,
  source: Record<string, unknown>,
): void {
  const fromRecord = parseFollowUpRecordPayload(
    source.follow_up_record ?? source.followUpRecord,
  )
  if (!fromRecord) return
  for (const [k, v] of Object.entries(fromRecord) as Array<
    [keyof CareOrderInspectionDetailPayload, CareOrderInspectionDetailPayload[keyof CareOrderInspectionDetailPayload]]
  >) {
    const existing = payload[k]
    if (Array.isArray(v)) {
      if (v.length && (!Array.isArray(existing) || !existing.length)) payload[k] = v as never
    } else if (typeof v === 'string' && v.trim() && (!existing || existing === '')) {
      payload[k] = v as never
    } else if (existing == null && v != null && v !== '') {
      payload[k] = v as never
    }
  }
}

function parseDetailPayload(raw: unknown): CareOrderInspectionDetailPayload | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return null
    try {
      return parseDetailPayload(JSON.parse(s) as unknown)
    } catch {
      return null
    }
  }
  if (typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const payload: CareOrderInspectionDetailPayload = {
    report_id: readStr(o, 'report_id', 'reportId'),
    follow_up_report_id: readStr(o, 'follow_up_report_id', 'followUpReportId', 'fu_report_id'),
    service_date: readStr(o, 'service_date', 'serviceDate', 'inspection_date', 'date'),
    service_time_text: readStr(
      o,
      'service_time_text',
      'serviceTimeText',
      'time_text',
      'inspection_time',
    ),
    assignment_text: readStr(
      o,
      'assignment_text',
      'assignmentText',
      'inspector',
      'technician_name',
      'inspector_name',
    ),
    inspector_name: readStr(o, 'inspector_name', 'inspectorName', 'technician_name'),
    status_text: readStr(o, 'status_text', 'statusText', 'status'),
    condition_rate: readStr(
      o,
      'condition_rate',
      'conditionRate',
      'rate',
      'score',
      'health_score',
      'healthScore',
      'inspection_rate',
      'inspectionRate',
      'condition_score',
      'conditionScore',
    ),
    condition_summary: readJoinedScalarValues(
      o.condition_summary ??
        o.conditionSummary ??
        o.condition_title ??
        o.condition_name ??
        o.selected_condition ??
        o.condition_value ??
        o.condition,
    ),
    condition: readJoinedScalarValues(
      o.condition ?? o.condition_text ?? o.condition_value ?? o.selected_condition,
    ),
    condition_note:
      readNoteValue(o.condition_note ?? o.conditionNote) ??
      readJoinedScalarValues(o.condition_description ?? o.condition_desc),
    location: readJoinedScalarValues(
      o.location ?? o.location_text ?? o.location_value ?? o.selected_location,
    ),
    location_detail: readJoinedScalarValues(
      o.location_detail ?? o.locationDetail ?? o.location_value ?? o.selected_location,
    ),
    location_note:
      readNoteValue(o.location_note ?? o.locationNote) ??
      readJoinedScalarValues(o.location_description),
    task: readJoinedScalarValues(o.task ?? o.task_text ?? o.task_value ?? o.selected_task),
    task_label: readJoinedScalarValues(
      o.task_label ?? o.taskLabel ?? o.task_value ?? o.selected_task,
    ),
    task_note:
      readNoteValue(o.task_note ?? o.taskNote) ??
      readJoinedScalarValues(o.task_description),
    time: readJoinedScalarValues(o.time ?? o.time_text ?? o.time_value ?? o.selected_time),
    time_label: readJoinedScalarValues(
      o.time_label ?? o.timeLabel ?? o.time_value ?? o.selected_time,
    ),
    time_note:
      readNoteValue(o.time_note ?? o.timeNote) ?? readJoinedScalarValues(o.time_description),
    general_description: readStrArray(
      o,
      'general_description',
      'generalDescription',
      'inspection_description',
      'inspection_note',
      'remarks',
      'remark',
    ),
    follow_up_date: readStr(o, 'follow_up_date', 'followUpDate'),
    follow_up_time_text: readStr(o, 'follow_up_time_text', 'followUpTimeText'),
    follow_up_technician: readStr(
      o,
      'follow_up_technician',
      'followUpTechnician',
      'follow_up_assignment_text',
    ),
    follow_up_notes: readStrArray(o, 'follow_up_notes', 'followUpNotes', 'follow_up_note'),
    photo_urls: readPhotoUrls(
      o,
      'photo_urls',
      'photoUrls',
      'photos',
      'images',
      'inspection_photos',
      'inspectionPhotos',
      'photo_list',
      'photoList',
      'image_list',
      'imageList',
      'attachments',
      'attachment_urls',
      'upload_images',
      'uploaded_images',
      'inspection_images',
      'media',
      'files',
    ),
    video_urls: readPhotoUrls(
      o,
      'video_urls',
      'videoUrls',
      'videos',
      'video_list',
      'videoList',
      'inspection_videos',
      'inspectionVideos',
    ),
    record_kind: inferRecordKind(o),
  }

  const technician = o.technician
  if (technician && typeof technician === 'object') {
    const techName = readStr(
      technician as Record<string, unknown>,
      'name',
      'label',
      'display_name',
      'displayName',
      'username',
    )
    if (techName) {
      assignPayloadString(payload, 'inspector_name', techName)
      assignPayloadString(payload, 'assignment_text', techName)
    }
  }

  for (const nestedKey of [
    'values',
    'inspection_values',
    'selected_values',
    'submitted_values',
    'checklist_values',
    'routine_values',
    'answer_values',
    'inspection_result_values',
    'item_values',
    'answer',
    'inspection_data',
    'result_data',
    'inspection_record',
  ] as const) {
    applyStructuredInspectionValues(payload, o[nestedKey])
  }
  applyStructuredInspectionValues(payload, o)
  applyComponentsInspectionValues(payload, o.components)

  const fromFormValues = parseFormValuesPayload(o.form_values)
  if (fromFormValues) {
    for (const [k, v] of Object.entries(fromFormValues) as Array<
      [keyof CareOrderInspectionDetailPayload, CareOrderInspectionDetailPayload[keyof CareOrderInspectionDetailPayload]]
    >) {
      const existing = payload[k]
      if (Array.isArray(v)) {
        if (v.length && (!Array.isArray(existing) || !existing.length)) payload[k] = v as never
      } else if (typeof v === 'string' && v.trim() && (!existing || existing === '')) {
        payload[k] = v as never
      } else if (existing == null && v != null && v !== '') {
        payload[k] = v as never
      }
    }
  }

  for (const nestedKey of [
    'inspection_detail',
    'inspection',
    'report',
    'detail',
    'inspection_result',
    'current',
    'current_detail',
    'inspection_record',
    'inspection_data',
    'result_data',
    'item_values',
  ] as const) {
    const nested = parseDetailPayload(o[nestedKey])
    if (!nested) continue
    for (const [k, v] of Object.entries(nested) as Array<
      [keyof CareOrderInspectionDetailPayload, CareOrderInspectionDetailPayload[keyof CareOrderInspectionDetailPayload]]
    >) {
      const existing = payload[k]
      if (Array.isArray(v)) {
        if (v.length && (!Array.isArray(existing) || !existing.length)) payload[k] = v as never
      } else if (typeof v === 'string' && v.trim() && (!existing || existing === '')) {
        payload[k] = v as never
      } else if (existing == null && v != null && v !== '') {
        payload[k] = v as never
      }
    }
  }

  if (!payload.photo_urls?.length) {
    payload.photo_urls = mergePhotoUrlLists(payload.photo_urls, collectPhotoUrlsDeep(o))
  }

  if (payload.video_urls?.length) {
    payload.photo_urls = mergePhotoUrlLists(payload.photo_urls, payload.video_urls)
  }

  applyFollowUpRecordFromObject(payload, o)

  const hasData = Object.values(payload).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? payload : null
}

function looksLikeInspectionHistoryArray(arr: unknown[]): boolean {
  let withDate = 0
  for (const item of arr.slice(0, 8)) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    if (
      readStr(
        o,
        'service_date',
        'serviceDate',
        'inspection_date',
        'date',
        'report_date',
        'create_time',
        'inspection_time',
        'service_date_time',
        'serviceDateTime',
      )
    ) {
      withDate++
    }
  }
  return withDate > 0
}

function looksLikeInspectionValuesArray(arr: unknown[]): boolean {
  for (const item of arr.slice(0, 5)) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    if (
      o.values != null ||
      o.inspection_values != null ||
      o.inspection_detail != null ||
      o.components != null ||
      o.condition != null ||
      o.location != null
    ) {
      return true
    }
  }
  return false
}

function historyListFromData(data: Record<string, unknown>): unknown[] {
  for (const key of [
    'history',
    'records',
    'inspection_history',
    'inspection_records',
    'history_list',
    'inspection_list',
    'past_records',
    'list',
    'dates',
  ] as const) {
    const v = data[key]
    if (!Array.isArray(v) || !v.length) continue
    if (looksLikeInspectionHistoryArray(v) || looksLikeInspectionValuesArray(v)) return v
  }
  return []
}

function parseHistoryEntry(raw: unknown, index: number): CareOrderInspectionHistoryEntry | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>

  if (
    o.result_id != null ||
    o.resultId != null ||
    (typeof o.date_text === 'string' && o.date_text.trim())
  ) {
    const adminEntry = parseAdminCareOrderHistoryEntry(raw, index)
    if (adminEntry) return adminEntry
  }

  const serviceDate =
    readStr(
      o,
      'date_text',
      'dateText',
      'service_date',
      'serviceDate',
      'inspection_date',
      'date',
      'report_date',
      'create_time',
      'update_time',
      'inspection_time',
      'service_date_time',
    ) ?? undefined
  const detail =
    parseDetailPayload(o) ??
    parseDetailPayload(o.inspection_detail) ??
    parseDetailPayload(o.inspection) ??
    parseDetailPayload(o.report) ??
    parseDetailPayload(o.values) ??
    parseDetailPayload(o.inspection_values)

  if (!serviceDate) {
    if (!detail) return null
    const key =
      readStr(o, 'key') ??
      `hist-detail-${readNum(o, 'id', 'order_id', 'inspection_id', 'record_id') ?? index}-${index}`
    const assignmentText =
      readStr(o, 'assignment_text', 'assignmentText', 'inspector', 'technician_name') ??
      detail?.assignment_text ??
      detail?.inspector_name
    const taskType = readCareOrderInspectionTaskType(o)
    return {
      key,
      orderPrimaryId: readNum(o, 'id', 'order_id', 'order_primary_id', 'care_order_id'),
      inspectionId: readNum(o, 'inspection_id', 'inspectionId', 'record_id'),
      serviceDate: detail?.service_date ?? '',
      serviceTimeText: detail?.service_time_text,
      assignmentText,
      recordKind: inferRecordKind(o),
      taskType,
      reportId: readStr(o, 'report_id', 'reportId'),
      detail,
    }
  }

  const orderPrimaryId = readNum(o, 'id', 'order_id', 'order_primary_id', 'care_order_id')
  const inspectionId =
    readNum(o, 'result_id', 'resultId', 'inspection_id', 'inspectionId', 'record_id')
  const key =
    readStr(o, 'key') ??
    `hist-${orderPrimaryId ?? inspectionId ?? index}-${serviceDate}-${index}`

  const assignmentText =
    readStr(o, 'assignment_text', 'assignmentText', 'inspector', 'technician_name') ??
    detail?.assignment_text ??
    detail?.inspector_name

  const taskType = readCareOrderInspectionTaskType(o)
  return {
    key,
    orderPrimaryId,
    inspectionId,
    serviceDate,
    serviceTimeText: readStr(o, 'service_time_text', 'serviceTimeText', 'time_text'),
    assignmentText,
    recordKind: inferRecordKind(o),
    taskType,
    reportId: readStr(o, 'report_id', 'reportId'),
    detail: detail ?? undefined,
  }
}

/** 解析 inspection-detail 原始响应 */
export function parseCareOrderInspectionDetailResponse(
  raw: unknown,
): CareOrderInspectionDetailResult | null {
  if (raw == null || raw === '') return null
  if (typeof raw !== 'object') return null

  const root = raw as Record<string, unknown>
  let data: Record<string, unknown> = root

  const code = root.code
  if (code === 200 || code === '200' || code === 0 || code === '0') {
    const nested = root.data
    if (Array.isArray(nested)) {
      const records: CareOrderInspectionHistoryEntry[] = []
      const seen = new Set<string>()
      for (const [i, item] of nested.entries()) {
        const entry = parseHistoryEntry(item, i)
        if (!entry || seen.has(entry.key)) continue
        seen.add(entry.key)
        records.push(entry)
      }
      let fromRecords: CareOrderInspectionDetailPayload | null = null
      for (const rec of records) {
        fromRecords = mergeInspectionPayloads(fromRecords, rec.detail)
      }
      const detail = mergeInspectionPayloads(fromRecords, parseDetailPayload(nested))
      if (!records.length && !detail) return null
      return { records, detail }
    }
    if (nested && typeof nested === 'object') {
      data = nested as Record<string, unknown>
    }
  }

  const records: CareOrderInspectionHistoryEntry[] = []
  const seen = new Set<string>()

  if (data.item && typeof data.item === 'object') {
    const itemObj = data.item as Record<string, unknown>
    const historyBags: unknown[] = []
    if (Array.isArray(data.history)) historyBags.push(...data.history)
    if (Array.isArray(itemObj.history)) historyBags.push(...itemObj.history)
    // history 可能为空，report_id / task_type 在 selected_history
    for (const sel of [data.selected_history, data.selectedHistory, itemObj.selected_history, itemObj.selectedHistory]) {
      if (sel && typeof sel === 'object' && !Array.isArray(sel)) historyBags.push(sel)
    }
    for (const [i, h] of historyBags.entries()) {
      const entry = parseAdminCareOrderHistoryEntry(h, i) ?? parseHistoryEntry(h, i)
      if (!entry || seen.has(entry.key)) continue
      seen.add(entry.key)
      records.push(entry)
    }
    const detail = mergeInspectionPayloads(
      parseCareOrderInspectionItemPayload(itemObj),
      parseFollowUpRecordPayload(data.follow_up_record ?? data.followUpRecord),
      parseFollowUpRecordPayload(itemObj.follow_up_record ?? itemObj.followUpRecord),
    )
    if (records.length || detail) return { records, detail }
  }

  for (const sel of [data.selected_history, data.selectedHistory]) {
    if (!sel || typeof sel !== 'object' || Array.isArray(sel)) continue
    const entry = parseAdminCareOrderHistoryEntry(sel, records.length) ?? parseHistoryEntry(sel, records.length)
    if (!entry || seen.has(entry.key)) continue
    seen.add(entry.key)
    records.push(entry)
  }

  for (const [i, item] of historyListFromData(data).entries()) {
    const entry = parseHistoryEntry(item, i)
    if (!entry || seen.has(entry.key)) continue
    seen.add(entry.key)
    records.push(entry)
  }

  let detail =
    parseDetailPayload(data.detail) ??
    parseDetailPayload(data.inspection_detail) ??
    parseDetailPayload(data.report) ??
    parseDetailPayload(data.current) ??
    parseDetailPayload(data.current_detail) ??
    parseDetailPayload(data.inspection) ??
    null

  if (!records.length) {
    const single = parseHistoryEntry(data, 0)
    if (single) {
      records.push(single)
      detail = mergeInspectionPayloads(single.detail, detail, parseDetailPayload(data))
    }
  }

  let fromRecords: CareOrderInspectionDetailPayload | null = null
  for (const rec of records) {
    fromRecords = mergeInspectionPayloads(fromRecords, rec.detail)
  }
  detail = mergeInspectionPayloads(fromRecords, detail)

  if (!detail) detail = parseDetailPayload(data)

  detail = mergeInspectionPayloads(
    detail,
    parseFollowUpRecordPayload(data.follow_up_record ?? data.followUpRecord),
  )

  if (!records.length && !detail) return null

  return { records, detail }
}

/** POST `/api/admin/care-order/inspection-detail` */
export async function postCareOrderInspectionDetail(
  body: CareOrderInspectionDetailRequest,
): Promise<CareOrderInspectionDetailResult> {
  const payload: Record<string, number> = {
    order_item_id: Math.trunc(body.order_item_id),
  }
  if (body.id != null && body.id > 0) {
    payload.id = Math.trunc(body.id)
  }
  if (body.result_id != null && body.result_id > 0) {
    payload.result_id = Math.trunc(body.result_id)
  }

  const raw = await request.post('/api/admin/care-order/inspection-detail', payload)
  const parsed = parseCareOrderInspectionDetailResponse(raw)
  if (parsed) return parsed

  const r = raw as { code?: number | string; msg?: string }
  const code = r?.code
  if (code === 200 || code === '200' || code === 0 || code === '0') {
    return { records: [], detail: null }
  }

  const msg =
    typeof r?.msg === 'string' && r.msg.trim() ? r.msg : 'Failed to load inspection detail'
  throw new Error(msg)
}
